<script setup>
import { computed, ref, useTemplateRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useVuelidate } from '@vuelidate/core';
import { whenever, useTimeoutPoll, useTimeoutFn } from '@vueuse/core';
import BaseModal from '../../headless/BaseModal.vue';
import TextHeadline from '../../atoms/app/text/TextHeadline.vue';
import TextBase from '../../atoms/app/text/TextBase.vue';
import AppLink from '../../atoms/app/AppLink.vue';
import CvSelection from '../CvSelection.vue';
import AppTile from '../../molecules/app/AppTile.vue';
import { useApplicationStore } from '../../../stores/application-store';
import AppButton from '../../atoms/app/AppButton.vue';
import FormCheckbox from '../../atoms/form/FormCheckbox.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import FormElement from '../../molecules/form/FormElement.vue';
import ExtractCvSuccess from '../../../assets/img/extract-cv-success.svg';
import CvExtractionFeature from '../../../assets/img/cv-extraction-feature.svg';
import CheckIcon from '../../../assets/img/icons/check.svg';
import CircularProgressLoadingSpinner from '../../atoms/progress/CircularProgressLoadingSpinner.vue';
import { extractCvFile, getExtractedCvFile } from '../../../services/extractCv';
import { useBreakpoints } from '../../../composables/breakpoint';
import { EVENTS, FUNNEL_STEP, FUNNEL_TYPES, track, trackVisibility } from '../../../utils/tracking';

const MODAL_CLOSE_DELAY = 2000;
const CV_POLLING_INTERVAL = 3000;
const CV_POLLING_TIMEOUT = 60000;

defineProps({
    dropzoneHtmlElement: {
        type: HTMLElement,
    },
});

const application = useApplicationStore();

const { isMobile } = useBreakpoints();

const {
    isCustomCvSelected,
    uploadedRawCvFile,
    cvExtractionUsed,
    extractedSuccess,
    extractedEmpty,
    extractedError,
} = storeToRefs(application);

const cvSelectionTileRef = useTemplateRef('cvSelectionTileComponent');
const cvSelectionRef = useTemplateRef('cvSelectionComponent');

const extractCvPolicy = ref(false);
const extractingOngoing = ref(false);
const extractingFinished = ref(false);
const startCvExtractionPolling = ref(null);

const extractingProcessStarted = computed(() => extractingOngoing.value || extractingFinished.value);
const cvUploadDone = computed(() => isCustomCvSelected.value && !extractingProcessStarted.value);

const cvSelectionTileHtmlElement = computed(() => cvSelectionTileRef.value?.$el);
const preventClosingModal = computed(() => extractingOngoing.value || cvSelectionRef.value?.currentlyUploading);

const open = defineModel('open', { type: Boolean });

const validations = {
    extractCvPolicy: {
        checked: isChecked => Boolean(isChecked),
    },
};
const v$ = useVuelidate(validations, { extractCvPolicy }, { $stopPropagation: true });

function fieldsAreEmpty(data) {
    const { phoneNumber } = data;
    const emptyFields = Object.entries(data)
        .filter(([key]) => key !== 'phoneNumber')
        .every(([, value]) => value === '');

    return (emptyFields && phoneNumber.nationalNumber === '');
}

function resetExtractingSteps() {
    extractingFinished.value = false;
    extractingOngoing.value = false;
    extractedSuccess.value = false;
    extractedEmpty.value = false;
    extractedError.value = false;

    extractCvPolicy.value = false;
    v$.value.$reset();
}

const {
    pause,
    resume,
} = useTimeoutPoll(
    // eslint-disable-next-line no-use-before-define
    () => fetchExtractedCv(application.applicationUuid),
    CV_POLLING_INTERVAL,
    { immediate: false },
);

const { start: startFailureTimeout, stop: stopFailureTimeout } = useTimeoutFn(() => {
    pause();

    // Make sure to show user that something went wrong if the request took longer than 60 seconds
    extractedError.value = true;
    open.value = false;

    track({
        element: 'AP_F: CV extraction failed',
        elementDetail: 'Timeout reached, could not extract data',
    });
}, CV_POLLING_TIMEOUT);

async function validateAndExtract() {
    v$.value.$touch();
    if (v$.value.$error) return;

    extractingOngoing.value = true;

    try {
        cvExtractionUsed.value = true;

        await extractCvFile(uploadedRawCvFile.value, application.applicationUuid);

        // Set the value so that the watcher triggers polling of fetching the Extracted CV data
        startCvExtractionPolling.value = true;
    } catch {
        extractedError.value = true;
        open.value = false;
        application.resetCvExtractionFields();

        track({
            element: 'AP_F: CV extraction failed',
            elementDetail: 'Service error',
        });
    }
}

const fetchExtractedCv = async () => {
    try {
        const response = await getExtractedCvFile(application.applicationUuid);
        const { status, extracted } = response.data;

        if (status === 'COMPLETED') {
            pause();
            stopFailureTimeout();

            if (fieldsAreEmpty(extracted)) {
                extractedEmpty.value = true;

                track({
                    element: 'AP_F: CV extraction failed',
                    elementDetail: 'Could not extract data',
                });
            } else {
                extractedSuccess.value = application.setExtractedData(extracted);

                track({
                    event: EVENTS.funnel,
                    funnelType: FUNNEL_TYPES.cvExtraction,
                    funnelStep: FUNNEL_STEP.cvExtractionSuccess,
                });
                track({
                    element: 'AP_F: CV extraction successful',
                });
            }

            extractingFinished.value = true;

            // Delay closing the modal after last step, so user can process all information displayed
            setTimeout(() => {
                open.value = false;
            }, MODAL_CLOSE_DELAY);
        } else if (status === 'FAILED') {
            pause();
            stopFailureTimeout();

            extractedError.value = true;
            open.value = false;
            application.resetCvExtractionFields();

            track({
                element: 'AP_F: CV extraction failed',
                elementDetail: 'Extraction with Vertex AI failed',
            });
        } else {
            extractingOngoing.value = true;
            extractingFinished.value = false;
        }
    } catch (err) {
        pause();

        extractedError.value = true;
        open.value = false;
        application.resetCvExtractionFields();

        track({
            element: 'AP_F: CV extraction failed',
            elementDetail: 'Service error',
        });
    } finally {
        startCvExtractionPolling.value = false;
    }
};

function closeModal() {
    if (!extractingFinished.value) { // "Modal closed" tracking: skip if automatically closed after extraction
        if (extractCvPolicy.value && cvUploadDone.value) {
            track({
                element: 'AP_F: CV extractor modal closed',
                elementDetail: 'After consent',
            });
        } else if (cvUploadDone.value) {
            track({
                element: 'AP_F: CV extractor modal closed',
                elementDetail: 'After upload before extraction',
            });
        } else {
            track({
                element: 'AP_F: CV extractor modal closed',
                elementDetail: 'Before upload',
            });
        }
    }
}

watch(isCustomCvSelected, (isSelected) => {
    if (!isSelected) resetExtractingSteps();
}, { immediate: true });

whenever(open, () => {
    resetExtractingSteps();

    track({
        event: EVENTS.funnel,
        funnelType: FUNNEL_TYPES.cvExtraction,
        funnelStep: FUNNEL_STEP.cvExtractionShowModal,
    });
    trackVisibility({
        element: 'AP_F: CV extractor modal shown',
    });
}, { immediate: true });

whenever(extractCvPolicy, () => {
    track({
        event: EVENTS.funnel,
        funnelType: FUNNEL_TYPES.cvExtraction,
        funnelStep: FUNNEL_STEP.cvExtractionConsent,
    });
});

// Starts polling the request to fetch Extracted CV data
whenever(startCvExtractionPolling, () => {
    resume();
    startFailureTimeout();
});

</script>

<template>
    <BaseModal
        v-model:open="open"
        panel-classes="max-w-[956px] px-0 py-0 rounded-md"
        :show-title="false"
        :prevent-dismiss="preventClosingModal"
        @after-leave="closeModal"
    >
        <template #default>
            <div class="flex flex-row">
                <div
                    v-if="!isMobile"
                    class="flex justify-center items-center flex-1 bg-purple-300 p-2xl m:max-w-[45%]"
                >
                    <CvExtractionFeature
                        class="max-w-[300px] w-full h-auto"
                    />
                </div>
                <div class="flex-1 relative min-h-[623px]">
                    <div class="s:px-xl m:px-[3rem] s:py-2xl m:py-[3.5rem]">
                        <TextHeadline
                            variant="lg-500"
                            class="mt-2"
                        >
                            Lebenslauf hochladen,<br>Bewerbung ausgefüllt.
                        </TextHeadline>
                        <TextBase
                            variant="lg-400"
                            class="mt-1"
                        >
                            Wir übernehmen deine Daten direkt aus dem Lebenslauf.
                        </TextBase>

                        <!-- Step 3 + 4: Extraction ongoing + Extraction done -->
                        <div
                            v-if="extractingProcessStarted"
                            class="flex flex-col justify-center items-center"
                        >
                            <transition
                                mode="out-in"
                                enter-active-class="transition-opacity duration-300 ease-in-out"
                                enter-from-class="opacity-0"
                                enter-to-class="opacity-100"
                                leave-active-class="transition-opacity duration-300 ease-in-out"
                                leave-from-class="opacity-100"
                                leave-to-class="opacity-0"
                            >
                                <div
                                    v-if="extractingFinished"
                                    data-qa="cv extraction finished illustration"
                                >
                                    <ExtractCvSuccess
                                        class="w-[248px] h-[265px] ml-[40px] mt-[33px]"
                                    />
                                    <span>Du bist startklar!</span>
                                </div>
                                <div
                                    v-else
                                    data-qa="cv analyzing illustration"
                                    class="flex flex-col items-center justify-center text-center"
                                >
                                    <CircularProgressLoadingSpinner
                                        class="w-[300px]"
                                    />
                                    <span>Dein Lebenslauf wird verarbeitet. Das dauert nur einen Moment.</span>
                                </div>
                            </transition>
                        </div>

                        <!-- Step 1: Default state => CV upload box -->
                        <AppTile
                            v-else
                            ref="cvSelectionTileComponent"
                            class="border border-gray-300 mt-2xl"
                            :class="{
                                'border-dashed': !isCustomCvSelected,
                                'border-solid': isCustomCvSelected,
                            }"
                        >
                            <CvSelection
                                ref="cvSelectionComponent"
                                :dropzone-html-element="cvSelectionTileHtmlElement"
                                inside-extract-cv-modal
                                no-spacing
                            />
                        </AppTile>

                        <!-- Step 2: CV upload done -->
                        <div
                            v-if="cvUploadDone"
                            class="mt-xl text-left"
                        >
                            <strong>Bitte gib noch dein Einverständnis</strong>
                            <FormElement>
                                <FormCheckbox
                                    v-model="extractCvPolicy"
                                    :error="v$.extractCvPolicy.$error"
                                    class="mt-s"
                                    @blur="v$.extractCvPolicy.$touch()"
                                >
                                    <p
                                        data-qa="extract cv policy text"
                                    >
                                        Ich stimme der Verarbeitung der Daten aus meinem Lebenslauf zum automatischen
                                        Ausfüllen des Bewerbungsformulars zu. Weitere Infos findest du in der
                                        <AppLink
                                            data-qa="extract cv policy link"
                                            class="link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="/datenschutzerklaerung#datenverarbeitung-und-unsere-produkte"
                                        >
                                            Datenschutzerklärung *
                                        </AppLink>
                                    </p>
                                </FormCheckbox>
                                <template #end>
                                    <FormMessage
                                        v-if="v$.extractCvPolicy.$error"
                                        type="error"
                                        data-qa="extract cv policy error"
                                    >
                                        Bitte akzeptiere die Datenschutzerklärung
                                    </FormMessage>
                                </template>
                            </FormElement>

                            <AppButton
                                class="w-full mt-2xl mb-xl"
                                data-qa="submit cv extraction"
                                @click="validateAndExtract"
                            >
                                Los geht's
                            </AppButton>
                        </div>

                        <!-- Step 1: Default state => bullet points -->
                        <ul
                            v-if="!isCustomCvSelected && !extractingProcessStarted"
                            class="mt-xl flex flex-col gap-y-xs text-left"
                        >
                            <li class="flex gap-m">
                                <CheckIcon
                                    class="flex-shrink-0"
                                />
                                Datenschutz: Deine Daten liegen in deiner Hand.
                            </li>
                            <li class="flex gap-m">
                                <CheckIcon
                                    class="flex-shrink-0"
                                />
                                Mehr Effizienz: Spare Zeit beim Bewerben.
                            </li>
                            <li class="flex gap-m">
                                <CheckIcon
                                    class="flex-shrink-0"
                                />
                                Fehler vermeiden: Keine Tippfehler dank automatischer Übernahme aus dem Lebenslauf.
                            </li>
                        </ul>
                        <AppLink
                            v-if="!isCustomCvSelected && !preventClosingModal"
                            tag="button"
                            light
                            class="w-fit flex mt-2xl m:pt-4xl s:mb-1 m:mb-2 text-sm"
                            data-qa="fill out form manually link"
                            @click="open = false"
                        >
                            Daten lieber manuell eintragen
                        </AppLink>
                    </div>
                </div>
            </div>
        </template>
    </BaseModal>
</template>
